import { Alert, Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import type { PremiseConfig } from "@shared/types/foyer";
import CallDirectoryEditor from "../components/CallDirectoryEditor";
import CallLogViewer from "../components/CallLogViewer";

/**
 * Care → Video calls (VC-10, VC-25).
 *
 * The tab strip is the page's skeleton, not decoration: this surface is who may
 * call and who rang. Directory (VC-10) is who may call; the Call log (VC-25) is
 * the read-only record of what actually happened. The calling *policy* (VC-12)
 * lives in Config → Video calls, next to the master on/off switch that gates
 * whether this whole section is even shown.
 *
 * Both tabs sit behind the SAME permission. Who may ring a resident's room and
 * who rang it are one safeguarding concern - splitting the rights would mean
 * someone could see the directory without being trusted to read the log of who
 * it connected.
 */
type VideoCallTab = "directory" | "log";

interface Props {
  premise: PremiseConfig;
  /** `canManageCallContacts` - the safeguarding permission, not general CMS edit. */
  canManage: boolean;
}

export default function VideoCallsPage({ premise, canManage }: Props) {
  const [tab, setTab] = useState<VideoCallTab>("directory");

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">Video calls</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 760 }}
        >
          Who a room can call, and who may call it.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v: VideoCallTab) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Directory" value="directory" data-testid="tab-directory" />
        <Tab label="Call log" value="log" data-testid="tab-log" />
      </Tabs>

      {!canManage ? (
        <Alert severity="info" data-testid="call-contacts-denied">
          You don’t have permission to manage this premise’s call directory or
          calling policy, or to read its call log. Deciding who may ring a
          resident’s room, under what rules, and seeing who did, is a separate
          right - ask an administrator for “Manage call contacts”.
        </Alert>
      ) : tab === "directory" ? (
        <CallDirectoryEditor
          premiseId={premise.premiseId}
          premiseTimezone={premise.address?.timezone}
        />
      ) : (
        <CallLogViewer premiseId={premise.premiseId} />
      )}
    </Stack>
  );
}
